/**
 * Features Content
 *
 * Varied section rhythms that match each surface (search, Context Bank,
 * Spaces, connectors, Nexus, creative) — not one icon-rail template.
 *
 * Primary responsibilities:
 * - Map Chat, search, Spaces, Context Bank, connectors, Nexus
 * - Drive signup / docs / Nexus CTAs with clean conversion rhythm
 *
 * Dependencies:
 * - marketing primitives
 * - marketing copy
 */

'use client'

import { cn } from '@/lib/utils'
import Link from 'next/link'
import { ArrowRightIcon } from '@radix-ui/react-icons'
import {
  IconAiAgents,
  IconBox,
  IconBrain,
  IconCards,
  IconDatabase,
  IconLayoutDashboard,
  IconListCheck,
  IconMessage,
  IconMicrophone,
  IconMusic,
  IconPlugConnected,
  IconRobot,
  IconSearch,
  IconShield,
  IconSitemap,
  IconSparkles,
  IconVideo,
  IconWorld
} from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'

import { CultTextureButton } from '@/components/marketing/cult-ui-components'
// Feature card copy lives in getMarketingCopy — do not call t('features.*') here.
// Marketing SSR often renders before i18n JSON is ready, which leaked raw keys
// like "features.core_features.ai_models.title" into the live /features HTML.
import { GeneratedMarketingAsset } from '@/components/marketing/generated-marketing-asset'
import { HomepageConnectors } from '@/components/marketing/homepage-connectors'
import { LosiAvatar } from '@/components/losi-bot/losi-avatar'
import {
  MarketingAssetOuterFade,
  marketingAssetSoftEdgeClassName
} from '@/components/marketing/marketing-asset-outer-fade'
import { MarketingReveal, MarketingFloat } from '@/components/marketing/marketing-motion'
import { MarketingShineBorder } from '@/components/marketing/marketing-shine-border'
import {
  MarketingContainer,
  MarketingPage,
  MarketingRailIcon
} from '@/components/marketing/marketing-primitives'
import { getMarketingCopy } from '@/lib/marketing-copy'

const productMapMeta = [
  { icon: IconMessage, href: '#chat' },
  { icon: IconSearch, href: '#search' },
  { icon: IconLayoutDashboard, href: '#spaces' },
  { icon: IconDatabase, href: '#context-bank' },
  { icon: IconPlugConnected, href: '#connectors' },
  { icon: IconAiAgents, href: '#nexus' }
] as const

const coreIcons = [IconBrain, IconWorld, IconSparkles, IconShield] as const
const creativeIcons = [IconVideo, IconMusic, IconMicrophone, IconBox] as const
const workflowIcons = [IconCards, IconListCheck, IconSitemap, IconRobot] as const

export function FeaturesContent() {
  const { i18n } = useTranslation()
  const copy = getMarketingCopy(i18n.resolvedLanguage || i18n.language)

  const coreRows = copy.features.core.cards.map((card, index) => ({
    ...card,
    icon: coreIcons[index] ?? coreIcons[0]
  }))

  const creativeRows = copy.features.creative.cards.map((card, index) => ({
    ...card,
    icon: creativeIcons[index] ?? creativeIcons[0]
  }))

  const workflowRows = copy.features.workflows.cards.map((card, index) => ({
    ...card,
    icon: workflowIcons[index] ?? workflowIcons[0]
  }))

  return (
    <MarketingPage>
      <MarketingContainer>
        {/* Hero — brand-first, one composition */}
        <section className="relative mx-auto max-w-[52rem] py-6 text-center lg:py-10">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
            {copy.features.eyebrow}
          </p>
          <h1 className="font-display mt-4 text-balance text-5xl font-medium leading-[0.96] tracking-[-0.02em] text-slate-950 sm:text-6xl dark:text-slate-50">
            {copy.features.title}
          </h1>
          <p className="mx-auto mt-5 max-w-[42rem] text-base leading-8 text-slate-600 sm:text-lg dark:text-slate-300">
            {copy.features.description}
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row sm:flex-wrap">
            <CultTextureButton asChild>
              <Link href="/login?register=true">
                {copy.common.startFree}
                <ArrowRightIcon className="ml-2 size-4" />
              </Link>
            </CultTextureButton>
            <CultTextureButton asChild tone="light">
              <Link href="/try">{copy.common.seeHowLosiWorks}</Link>
            </CultTextureButton>
          </div>
        </section>

        {/* Product map — jump links into the rest of the page */}
        <section className="relative mt-10 py-12 lg:mt-14 lg:py-16">
          <div className="mx-auto max-w-[40rem] text-center">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              {copy.features.productMap.eyebrow}
            </p>
            <h2 className="font-display mt-4 text-4xl font-medium leading-[0.98] tracking-[-0.02em] text-slate-950 sm:text-5xl dark:text-slate-50">
              {copy.features.productMap.title}
            </h2>
            <p className="mx-auto mt-4 max-w-[40ch] text-base leading-8 text-slate-600 dark:text-slate-300">
              {copy.features.productMap.description}
            </p>
          </div>
          <nav
            aria-label={copy.features.productMap.eyebrow}
            className="mx-auto mt-12 grid max-w-[64rem] gap-0 border-y border-slate-200/80 dark:border-white/10 sm:grid-cols-2 lg:grid-cols-3"
          >
            {copy.features.productMap.items.map((item, index) => {
              const meta = productMapMeta[index] || productMapMeta[0]
              const Icon = meta.icon

              return (
                <MarketingReveal key={item.title} delay={0.05 * index} distance={18} blurPx={7}>
                  <Link
                    href={meta.href}
                    className="group flex flex-col gap-3 border-b border-slate-200/80 px-5 py-7 transition-colors last:border-b-0 hover:bg-slate-50/80 dark:border-white/10 dark:hover:bg-white/[0.03] sm:odd:border-r lg:border-r lg:[&:nth-child(3n)]:border-r-0 lg:[&:nth-child(n+4)]:border-b-0 sm:[&:nth-last-child(-n+2)]:border-b-0"
                  >
                    <span className="flex items-center justify-between gap-3">
                      <MarketingRailIcon icon={Icon} />
                      <ArrowRightIcon className="size-4 text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:text-slate-600 dark:text-slate-600 dark:group-hover:text-slate-300" />
                    </span>
                    <h3 className="text-lg font-semibold tracking-tight text-slate-950 dark:text-slate-50">
                      {item.title}
                    </h3>
                    <p className="max-w-[36ch] text-sm leading-7 text-slate-600 dark:text-slate-300">
                      {item.description}
                    </p>
                  </Link>
                </MarketingReveal>
              )
            })}
          </nav>
        </section>

        {/* Search — feel like search: query chrome + result lines */}
        <section
          id="search"
          className="relative mt-4 scroll-mt-28 overflow-hidden py-12 lg:mt-8 lg:py-16"
        >
          <div className="grid items-center gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:gap-14">
            <div className="flex max-w-[40rem] flex-col gap-5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                {copy.features.search.eyebrow}
              </p>
              <h2 className="font-display text-4xl font-medium leading-[0.98] tracking-[-0.02em] text-slate-950 sm:text-5xl dark:text-slate-50">
                {copy.features.search.title}
              </h2>
              <p className="text-base leading-8 text-slate-600 sm:text-lg dark:text-slate-300">
                {copy.features.search.description}
              </p>

              <div className="mt-2 overflow-hidden rounded-[1.4rem] border border-slate-200/90 bg-white shadow-[inset_0_1px_0_rgba(255,255,255,0.9)] dark:border-white/10 dark:bg-[rgba(11,14,18,0.96)] dark:shadow-none">
                <div className="flex items-center gap-3 border-b border-slate-200/80 px-4 py-3.5 dark:border-white/10">
                  <IconSearch className="size-4 shrink-0 text-slate-400" stroke={1.5} />
                  <p className="truncate text-sm text-slate-500 dark:text-slate-400">
                    Find a Space, file, memory, or chat…
                  </p>
                </div>
                <ul className="divide-y divide-slate-200/80 dark:divide-white/10">
                  {copy.features.search.cards.map(card => (
                    <li key={card.title} className="px-4 py-4">
                      <p className="text-sm font-semibold text-slate-950 dark:text-slate-50">
                        {card.title}
                      </p>
                      <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">
                        {card.description}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className={cn("relative isolate min-h-[280px] overflow-hidden sm:min-h-[340px] lg:min-h-[400px]", marketingAssetSoftEdgeClassName)}>
              <GeneratedMarketingAsset
                assetKey="blogCollage"
                frame={false}
                presentation="open"
                className="absolute inset-0"
                imageClassName="object-contain object-center opacity-95"
                sizes="(min-width: 1024px) 560px, 100vw"
              />
              <MarketingAssetOuterFade />
            </div>
          </div>
        </section>

        {/* Context Bank — dark break; capability stream, not icon list */}
        <section
          id="context-bank"
          className="relative mt-4 scroll-mt-28 overflow-visible py-12 lg:mt-6 lg:py-16"
        >
          <MarketingShineBorder>
            <div className="px-6 py-12 text-slate-50 sm:px-8 sm:py-14 lg:px-12 lg:py-16">
              <div className="grid items-center gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-12">
                <div className="flex max-w-[40rem] flex-col gap-5">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">
                    {copy.features.contextBank.eyebrow}
                  </p>
                  <h2 className="font-display text-4xl font-medium leading-[0.98] tracking-[-0.02em] text-white sm:text-5xl">
                    {copy.features.contextBank.title}
                  </h2>
                  <p className="text-base leading-8 text-slate-300 sm:text-lg">
                    {copy.features.contextBank.description}
                  </p>
                  <p className="text-sm leading-7 text-slate-400">
                    {copy.home.contextBank.capabilities.join(' · ')}
                  </p>
                  <div className="mt-1 space-y-5 border-t border-white/10 pt-6">
                    {copy.features.contextBank.cards.map(card => (
                      <div key={card.title}>
                        <h3 className="text-lg font-semibold text-white">{card.title}</h3>
                        <p className="mt-1.5 text-sm leading-7 text-slate-300">
                          {card.description}
                        </p>
                      </div>
                    ))}
                  </div>
                  <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:flex-wrap sm:items-center">
                    <CultTextureButton asChild>
                      <Link href="/articles/use-context-bank-across-tools">
                        {copy.features.contextBank.primary}
                        <ArrowRightIcon className="ml-2 size-4" />
                      </Link>
                    </CultTextureButton>
                    <Link
                      href="/login?register=true"
                      className="inline-flex items-center text-sm font-medium text-slate-200 transition-colors hover:text-white"
                    >
                      {copy.features.contextBank.secondary}
                      <ArrowRightIcon className="ml-1.5 size-3.5" />
                    </Link>
                  </div>
                </div>
                <div className={cn("relative isolate min-h-[240px] overflow-hidden sm:min-h-[300px] lg:min-h-[340px]", marketingAssetSoftEdgeClassName)}>
                  <GeneratedMarketingAsset
                    assetKey="spacesStack"
                    frame={false}
                    presentation="open"
                    className="absolute inset-0"
                    imageClassName="object-contain object-center opacity-95"
                    sizes="(min-width: 1024px) 520px, 100vw"
                  />
                  <MarketingAssetOuterFade tone="dark-plate" />
                </div>
              </div>
            </div>
          </MarketingShineBorder>
        </section>

        {/* Spaces — visual-led, short statements */}
        <section
          id="spaces"
          className="relative mt-4 scroll-mt-28 overflow-hidden py-12 lg:mt-8 lg:py-16"
        >
          <div className="grid items-center gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-14">
            <div className={cn("relative isolate order-2 min-h-[280px] overflow-hidden sm:min-h-[360px] lg:order-1 lg:min-h-[420px]", marketingAssetSoftEdgeClassName)}>
              <GeneratedMarketingAsset
                assetKey="spacesStack"
                frame={false}
                presentation="open"
                className="absolute inset-0"
                imageClassName="object-contain object-center opacity-95"
                sizes="(min-width: 1024px) 520px, 100vw"
              />
              <MarketingAssetOuterFade />
            </div>
            <div className="order-1 flex max-w-[40rem] flex-col gap-5 lg:order-2">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                {copy.features.spaces.eyebrow}
              </p>
              <h2 className="font-display text-4xl font-medium leading-[0.98] tracking-[-0.02em] text-slate-950 sm:text-5xl dark:text-slate-50">
                {copy.features.spaces.title}
              </h2>
              <p className="text-base leading-8 text-slate-600 sm:text-lg dark:text-slate-300">
                {copy.features.spaces.description}
              </p>
              <div className="mt-1 space-y-5 border-t border-slate-200/80 pt-6 dark:border-white/10">
                {copy.features.spaces.cards.map(card => (
                  <div key={card.title}>
                    <h3 className="text-lg font-semibold tracking-tight text-slate-950 dark:text-slate-50">
                      {card.title}
                    </h3>
                    <p className="mt-1.5 text-sm leading-7 text-slate-600 dark:text-slate-300">
                      {card.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <div id="connectors" className="scroll-mt-28">
          <HomepageConnectors
            eyebrow={copy.home.connectors.eyebrow}
            heading={copy.home.connectors.heading}
            subhead={copy.home.connectors.subhead}
            rowOne={copy.home.connectors.rowOne}
            rowTwo={copy.home.connectors.rowTwo}
          />
        </div>

        {/* Nexus — operations visual + agent presence */}
        <section
          id="nexus"
          className="relative mt-4 scroll-mt-28 overflow-hidden py-12 lg:mt-8 lg:py-16"
        >
          <div className="grid items-center gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:gap-14">
            <div className="flex max-w-[40rem] flex-col gap-5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                {copy.features.nexus.eyebrow}
              </p>
              <h2 className="font-display text-4xl font-medium leading-[0.98] tracking-[-0.02em] text-slate-950 sm:text-5xl dark:text-slate-50">
                {copy.features.nexus.title}
              </h2>
              <p className="text-base leading-8 text-slate-600 sm:text-lg dark:text-slate-300">
                {copy.features.nexus.description}
              </p>
              <p className="text-sm leading-7 text-slate-500 dark:text-slate-400">
                {copy.features.nexus.cards.map(card => card.title).join(' · ')}
              </p>
              <div className="space-y-4 border-t border-slate-200/80 pt-6 dark:border-white/10">
                {copy.features.nexus.cards.map(card => (
                  <p key={card.title} className="text-sm leading-7 text-slate-600 dark:text-slate-300">
                    <span className="font-semibold text-slate-950 dark:text-slate-50">
                      {card.title}.
                    </span>{' '}
                    {card.description}
                  </p>
                ))}
              </div>
              <div className="pt-2">
                <CultTextureButton asChild>
                  <Link href="/agents">
                    {copy.common.seeLosiNexus}
                    <ArrowRightIcon className="ml-2 size-4" />
                  </Link>
                </CultTextureButton>
              </div>
            </div>
            <div className={cn("relative isolate min-h-[280px] overflow-hidden sm:min-h-[340px] lg:min-h-[400px]", marketingAssetSoftEdgeClassName)}>
              <GeneratedMarketingAsset
                assetKey="nexusSignals"
                frame={false}
                presentation="open"
                className="absolute inset-0"
                imageClassName="object-contain object-center opacity-95"
                sizes="(min-width: 1024px) 560px, 100vw"
              />
              <MarketingAssetOuterFade />
              <MarketingFloat
                amplitude={5}
                duration={6}
                className="absolute right-[18%] top-[14%] z-10"
              >
                <LosiAvatar
                  state="agentChat"
                  size={56}
                  fitBody
                  followCursor
                  className="drop-shadow-sm"
                />
              </MarketingFloat>
            </div>
          </div>
        </section>

        {/* Chat & creation — sticky + rows with icons that match the capability */}
        <section
          id="chat"
          className="relative mt-8 scroll-mt-28 py-12 lg:mt-12 lg:py-16"
        >
          <div className="grid gap-10 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] lg:gap-14">
            <div className="flex max-w-[34rem] flex-col gap-4 lg:sticky lg:top-28 lg:self-start">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                {copy.features.core.eyebrow}
              </p>
              <h2 className="font-display text-4xl font-medium leading-[1.02] tracking-[-0.02em] text-slate-950 sm:text-5xl dark:text-slate-50">
                {copy.features.core.title}
              </h2>
              <p className="text-base leading-8 text-slate-600 dark:text-slate-300">
                {copy.features.core.description}
              </p>
            </div>
            <div className="flex flex-col">
              {coreRows.map(item => (
                <article
                  key={item.title}
                  className="grid gap-4 border-t border-slate-200/80 py-7 first:border-t-0 first:pt-0 sm:grid-cols-[3.5rem_minmax(0,1fr)] sm:gap-6 dark:border-white/10"
                >
                  <MarketingRailIcon icon={item.icon} />
                  <div>
                    <h3 className="text-xl font-semibold tracking-tight text-slate-950 dark:text-slate-50">
                      {item.title}
                    </h3>
                    <p className="mt-2 max-w-[42ch] text-sm leading-7 text-slate-600 dark:text-slate-300">
                      {item.description}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Creative — studio visual dominates; tools as a studio strip */}
        <section className="relative mt-4 overflow-hidden py-12 lg:mt-8 lg:py-16">
          <div className="grid items-end gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-14">
            <div className={cn("relative isolate min-h-[260px] overflow-hidden sm:min-h-[340px] lg:min-h-[420px]", marketingAssetSoftEdgeClassName)}>
              <GeneratedMarketingAsset
                assetKey="creativeStudio"
                frame={false}
                presentation="open"
                className="absolute inset-0"
                imageClassName="object-contain object-center opacity-95"
                sizes="(min-width: 1024px) 560px, 100vw"
              />
              <MarketingAssetOuterFade />
            </div>
            <div className="flex max-w-[40rem] flex-col gap-5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                {copy.features.creative.eyebrow}
              </p>
              <h2 className="font-display text-4xl font-medium leading-[0.98] tracking-[-0.02em] text-slate-950 sm:text-5xl dark:text-slate-50">
                {copy.features.creative.title}
              </h2>
              <p className="text-base leading-8 text-slate-600 sm:text-lg dark:text-slate-300">
                {copy.features.creative.description}
              </p>
              <ul className="mt-2 grid gap-0 border-t border-slate-200/80 dark:border-white/10 sm:grid-cols-2">
                {creativeRows.map((item, index) => (
                  <li
                    key={item.title}
                    className={`border-slate-200/80 py-5 dark:border-white/10 ${
                      index % 2 === 0 ? 'sm:pr-6 sm:border-r' : 'sm:pl-6'
                    } ${index < 2 ? 'border-b' : ''}`}
                  >
                    <MarketingRailIcon icon={item.icon} size="sm" />
                    <h3 className="mt-3 text-base font-semibold tracking-tight text-slate-950 dark:text-slate-50">
                      {item.title}
                    </h3>
                    <p className="mt-1.5 text-sm leading-6 text-slate-600 dark:text-slate-300">
                      {item.description}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Workflows — horizontal process across the page */}
        <section className="relative mt-4 py-12 lg:mt-8 lg:py-16">
          <div className="mx-auto max-w-[46rem] text-center">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              {copy.features.workflows.eyebrow}
            </p>
            <h2 className="font-display mt-4 text-4xl font-medium leading-[0.98] tracking-[-0.02em] text-slate-950 sm:text-5xl dark:text-slate-50">
              {copy.features.workflows.title}
            </h2>
            <p className="mx-auto mt-4 max-w-[40ch] text-base leading-8 text-slate-600 dark:text-slate-300">
              {copy.features.workflows.description}
            </p>
          </div>
          <ol className="mt-12 grid gap-0 border-y border-slate-200/80 dark:border-white/10 sm:grid-cols-2 lg:grid-cols-4">
            {workflowRows.map((item, index) => (
              <li
                key={item.title}
                className={`relative flex flex-col gap-3 px-5 py-8 ${
                  index > 0
                    ? 'border-t border-slate-200/80 dark:border-white/10 sm:border-t-0 sm:border-l'
                    : ''
                }`}
              >
                <MarketingRailIcon icon={item.icon} />
                <p className="text-[11px] font-semibold tabular-nums tracking-[0.18em] text-slate-400">
                  {String(index + 1).padStart(2, '0')}
                </p>
                <h3 className="text-lg font-semibold tracking-tight text-slate-950 dark:text-slate-50">
                  {item.title}
                </h3>
                <p className="text-sm leading-7 text-slate-600 dark:text-slate-300">
                  {item.description}
                </p>
              </li>
            ))}
          </ol>
        </section>

        {/* Close lives in SiteFooter — avoid stacking the same CTA twice. */}
      </MarketingContainer>
    </MarketingPage>
  )
}
